import React, { useState, useEffect, useRef } from "react";
import { FaCalendarAlt, FaArrowLeft, FaChevronDown } from "react-icons/fa";
import "../assets/css/AddUser.css";
import "../assets/css/DigixLeads.css";
import axiosInstance from "../components/api/axiosInstance";
import GoogleAddressSearch from "../components/GoogleAddressSearch";
import { resolveImageUrl } from "../config/apiConfig";
import { getAuthItem, setAuthItem } from "../utils/authStorage";
import { PASSWORD_POLICY_HINT, validatePasswordStrength } from "../utils/passwordPolicy";
import {
  DEFAULT_COUNTRY_CODE,
  formValueWithMobileSanitize,
  mobileValidationMessage,
  normalizeCountryCode,
} from "../utils/phone";
import CountryCodeSelect from "../components/CountryCodeSelect";
import TenantCompanySelect from "../components/TenantCompanySelect";
import OwnerPasswordField from "../components/OwnerPasswordField";
import { getStaffLoginUrl } from "../config/ctxPath";
import { aadhaarError, applyPascalNameFields, formatPascal, personalEmailError, roleDisplayName } from "../utils/formatters";
import ReportingLineFields from "./work/ReportingLineFields";
import DepartmentSelect from "./work/DepartmentSelect";
import { staffWorkApi } from "./work/workApi";

const LEAVING_REQUIRED_STATUSES = ["Resigned", "Terminated"];

const DEFAULT_ROLES = [
  { role_id: 1, role_name: "Admin" },
  { role_id: 2, role_name: "Manager" },
  { role_id: 3, role_name: "Developer" },
  { role_id: 4, role_name: "Tester" },
  { role_id: 5, role_name: "Business Analyst" },
  { role_id: 6, role_name: "Tenant" },
  { role_id: 7, role_name: "HR" },
  { role_id: 8, role_name: "Devops Engineer" },
  { role_id: 9, role_name: "Relationship Manager" },
];

function normalizeRolesFromApi(data) {
  const raw = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.roles)
        ? data.roles
        : [];
  return raw
    .map((r) => ({
      role_id: r.role_id ?? r.roleId ?? r.Designation_Id ?? r.designation_id ?? r.id ?? 0,
      role_name: (
        r.role_name ??
        r.roleName ??
        r.Designation_Name ??
        r.designation_name ??
        r.name ??
        r.role ??
        ""
      ).toString().trim() || "—"
    }))
    .filter((r) => r.role_id != null && r.role_id !== "");
}

function normalizeDesignationsFromApi(data) {
  const raw = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : [];
  return raw
    .map((d) => ({
      designation_id:
        d.Designation_Id ?? d.designation_id ?? d.id ?? 0,
      designation_name:
        (d.Designation_Name ?? d.designation_name ?? d.name ?? "").toString().trim() || "—"
    }))
    .filter((d) => d.designation_id != null && d.designation_id !== "");
}

function EditUser({ userId, onBack, onUpdate }) {
  const reportingRef = useRef(null);
  const dateOfBirthRef = useRef(null);
  const dateOfJoiningRef = useRef(null);
  const dateOfLeavingRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    gender: "",
    countryCode: DEFAULT_COUNTRY_CODE,
    phoneNumber: "",
    altCountryCode: DEFAULT_COUNTRY_CODE,
    altNumber: "",
    aadhaar: "",
    pan: "",
    dateOfBirth: "",
    addressLine1: "",
    addressLine2: "",
    area: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    formattedAddress: "",
    company: "",
    companyEmail: "",
    employeeId: "",
    companyClientId: "",
    // Multi-role selection (primary role + list)
    role: "",
    roleId: "",
    roleIds: [],
    designationId: "",
    department: "",
    band: "",
    dateOfJoining: "",
    dateOfLeaving: "",
    status: "",
    companyLocation: "",
    profileImage: null,
    password: "",
    currentImageUrl: ""
  });

  const [rolesList, setRolesList] = useState(DEFAULT_ROLES);
  const [designationsList, setDesignationsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [designationDropdownOpen, setDesignationDropdownOpen] = useState(false);
  const [designationSearchQuery, setDesignationSearchQuery] = useState("");
  const designationDropdownRef = useRef(null);

  const genders = [
    "Select Gender",
    "Male",
    "Female",
    "Other",
    "Prefer not to say"
  ];

  const bands = [
    "Select Band",
    "A",
    "B",
    "C",
    "D",
    "E",
    "Not Assigned"
  ];

  const statuses = [
    "Select Status",
    "Active",
    "Inactive",
    "Resigned",
    "Terminated",
    "On Hold"
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rolesResponse, designationsResponse] = await Promise.all([
          axiosInstance.get("/roles"),
          axiosInstance.get("/designations")
        ]);

        const roles = normalizeRolesFromApi(rolesResponse?.data);
        if (roles.length > 0) {
          setRolesList(roles);
        }

        const designations = normalizeDesignationsFromApi(designationsResponse?.data);
        if (designations.length > 0) {
          setDesignationsList(designations);
        }
      } catch (err) {
        console.error(
          "Error fetching roles/designations:",
          err.response?.data?.message || err.message || err
        );
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        designationDropdownRef.current &&
        !designationDropdownRef.current.contains(e.target)
      ) {
        setDesignationDropdownOpen(false);
        setDesignationSearchQuery("");
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axiosInstance.get(`/user/${userId}`);
        if (response.data) {
          const user = response.data;
          const imageUrl = (user.image_url || "").length ? resolveImageUrl(user.image_url) : "";

          const primaryRoleName =
            user.role_name ||
            user.Role_Name ||
            user.role ||
            (Array.isArray(user.roles) && (user.roles[0]?.role_name || user.roles[0]?.roleName)) ||
            "";
          const primaryRoleId =
            user.role_id ||
            user.Role_Id ||
            (Array.isArray(user.role_ids) && user.role_ids[0]) ||
            (Array.isArray(user.Role_Ids) && user.Role_Ids[0]) ||
            (Array.isArray(user.roles) && user.roles[0]?.role_id) ||
            "";

          // Normalize role IDs for multi-select
          const explicitRoleIds = Array.isArray(user.role_ids)
            ? user.role_ids
            : Array.isArray(user.Role_Ids)
              ? user.Role_Ids
              : [];
          const rolesFromArray = Array.isArray(user.roles) ? user.roles : [];
          const roleIdsSet = new Set(
            explicitRoleIds
              .map((id) => (id != null ? String(id) : null))
              .filter((id) => id !== null)
          );
          rolesFromArray.forEach((r) => {
            const id = r.role_id ?? r.roleId ?? r.id;
            if (id != null) {
              roleIdsSet.add(String(id));
            }
          });
          const roleIdsArr = Array.from(roleIdsSet);
          const multiRoleLabel =
            roleIdsArr.length === 0
              ? ""
              : roleIdsArr.length === 1
                ? primaryRoleName
                : `${roleIdsArr.length} roles selected`;
          
          setFormData({
            firstName: user.first_name || "",
            lastName: user.last_name || "",
            email: user.email || "",
            gender: user.gender || "",
            countryCode: normalizeCountryCode(user.country_code),
            phoneNumber: user.phone_number || "",
            altCountryCode: normalizeCountryCode(user.country_code),
            altNumber: user.alt_number || "",
            aadhaar: user.aadhar || user.aadhaar || "",
            pan: user.pan || user.pan_number || "",
            dateOfBirth: user.dob || "",
            addressLine1: user.address_line_1 || user.Address_Line_1 || "",
            addressLine2: user.address_line_2 || user.Address_Line_2 || "",
            area: user.area || user.Area || "",
            city: user.city || user.City || "",
            state: user.state || user.State || "",
            country: user.country || user.Country || "",
            pincode: user.pincode || user.Pincode || "",
            formattedAddress: [
              user.address_line_1 || user.Address_Line_1,
              user.address_line_2 || user.Address_Line_2,
              user.area || user.Area,
              user.city || user.City,
              user.state || user.State,
              user.country || user.Country,
              user.pincode || user.Pincode,
            ]
              .filter(Boolean)
              .join(", ") || user.address || "",
            company: user.company_name || "",
            companyEmail: user.company_email || "",
            employeeId: user.employee_id || "",
            role: multiRoleLabel,
            roleId: primaryRoleId ? String(primaryRoleId) : "",
            roleIds: roleIdsArr,
            designationId: user.designation_id || user.Designation_Id || "",
            department: user.department || "",
            band: user.band || "",
            dateOfJoining: user.doj || user.date_of_joining || "",
            dateOfLeaving: user.date_of_leaving || "",
            status: user.status || "",
            companyLocation: user.company_location || "",
            profileImage: null,
            password: "",
            currentImageUrl: imageUrl
          });
        }
      } catch (error) {
        console.error("Error fetching user:", error.response?.data?.message || error.message || error);
        setError(error.response?.data?.message || "Failed to load user details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUser();
    }
  }, [userId]);

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === "file") {
      setFormData(prev => ({
        ...prev,
        [name]: files[0] || null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: formValueWithMobileSanitize(name, value)
      }));
    }
  };

  const updateRoleSelection = (newRoleIds) => {
    const uniqueIds = Array.from(new Set(newRoleIds));
    const selectedRoles = rolesList.filter((r) =>
      uniqueIds.includes(String(r.role_id))
    );
    const roleLabel =
      selectedRoles.length === 0
        ? ""
        : selectedRoles.length === 1
          ? selectedRoles[0].role_name
          : `${selectedRoles.length} roles selected`;

    setFormData((prev) => ({
      ...prev,
      roleIds: uniqueIds,
      role: roleLabel
    }));
  };

  const handleToggleRole = (roleId) => {
    const idStr = String(roleId);
    setFormData((prev) => {
      const current = Array.isArray(prev.roleIds) ? prev.roleIds : [];
      const exists = current.includes(idStr);
      const next = exists ? current.filter((id) => id !== idStr) : [...current, idStr];
      const selectedRoles = rolesList.filter((r) => next.includes(String(r.role_id)));
      const roleLabel =
        selectedRoles.length === 0
          ? ""
          : selectedRoles.length === 1
            ? selectedRoles[0].role_name
            : `${selectedRoles.length} roles selected`;
      return {
        ...prev,
        roleIds: next,
        role: roleLabel
      };
    });
  };

  const handleToggleSelectAllRoles = () => {
    if (!rolesList.length) return;
    const allIds = rolesList.map((r) => String(r.role_id));
    const current = Array.isArray(formData.roleIds) ? formData.roleIds : [];
    if (current.length === rolesList.length) {
      updateRoleSelection([]);
    } else {
      updateRoleSelection(allIds);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validate required fields for API
    if (!formData.firstName || !formData.lastName || !formData.email || 
        !formData.companyEmail ||
        !Array.isArray(formData.roleIds) || formData.roleIds.length === 0 ||
        !formData.department) {
      alert("Please fill in all required fields (*)");
      return;
    }

    const fieldIssue = personalEmailError(formData.email, formData.companyEmail) || aadhaarError(formData.aadhaar);
    if (fieldIssue) {
      setError(fieldIssue);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (LEAVING_REQUIRED_STATUSES.includes(formData.status) && !formData.dateOfLeaving) {
      alert(`Date of Leaving is required when status is ${formData.status}.`);
      return;
    }

    const phoneError = mobileValidationMessage(formData.phoneNumber, { label: "Phone number" });
    if (phoneError) {
      alert(phoneError);
      return;
    }
    const altError = mobileValidationMessage(formData.altNumber, { label: "Alternate phone" });
    if (altError) {
      alert(altError);
      return;
    }

    if (formData.password) {
      const passwordError = validatePasswordStrength(formData.password);
      if (passwordError) {
        alert(passwordError);
        return;
      }
    }

    setSubmitting(true);
    try {
      let response;
      const selectedRoleIds = (Array.isArray(formData.roleIds) ? formData.roleIds : [])
        .map((id) => parseInt(id, 10))
        .filter((n) => !Number.isNaN(n));
      const primaryRoleId = selectedRoleIds[0];
      
      // If an image is selected, use FormData for multipart/form-data upload
      if (formData.profileImage) {
        const formDataToSend = new FormData();
        
        // Append the image file with the field name 'image_path' as required by API
        formDataToSend.append('image_path', formData.profileImage);
        
        // Append all other fields
        formDataToSend.append('first_name', formatPascal(formData.firstName));
        formDataToSend.append('last_name', formatPascal(formData.lastName));
        formDataToSend.append('email', formData.email);
        formDataToSend.append('company_email', formData.companyEmail);
        if (primaryRoleId != null) {
          // Support both legacy role_id and new role_ids array
          formDataToSend.append('role_id', primaryRoleId);
          formDataToSend.append('role_ids', JSON.stringify(selectedRoleIds));
        }
        if (formData.designationId) {
          formDataToSend.append('designation_id', parseInt(formData.designationId, 10));
        }
        formDataToSend.append('department', formData.department);
        
        if (formData.status) formDataToSend.append('status', formData.status);
        if (formData.gender) formDataToSend.append('gender', formData.gender);
        if (formData.phoneNumber) {
          formDataToSend.append('phone_number', formData.phoneNumber);
          formDataToSend.append('country_code', formData.countryCode);
        }
        if (formData.altNumber) formDataToSend.append('alt_number', formData.altNumber);
        if (formData.aadhaar) formDataToSend.append('aadhar', formData.aadhaar);
        if (formData.pan) formDataToSend.append('pan', formData.pan);
        if (formData.dateOfBirth) formDataToSend.append('dob', formData.dateOfBirth);
        formDataToSend.append('address_line_1', formData.addressLine1 || "");
        formDataToSend.append('address_line_2', formData.addressLine2 || "");
        formDataToSend.append('area', formData.area || "");
        formDataToSend.append('city', formData.city || "");
        formDataToSend.append('state', formData.state || "");
        formDataToSend.append('country', formData.country || "");
        formDataToSend.append('pincode', formData.pincode || "");
        if (formData.company) formDataToSend.append('company_name', formatPascal(formData.company));
        if (formData.companyClientId) formDataToSend.append('company_client_id', formData.companyClientId);
        if (formData.band) formDataToSend.append('band', formData.band);
        if (formData.dateOfJoining) {
          formDataToSend.append('doj', formData.dateOfJoining);
          formDataToSend.append('date_of_joining', formData.dateOfJoining);
        }
        if (formData.dateOfLeaving) formDataToSend.append('date_of_leaving', formData.dateOfLeaving);
        if (formData.companyLocation) formDataToSend.append('company_location', formData.companyLocation);
        if (formData.password) formDataToSend.append('password', formData.password);
        
        response = await axiosInstance.put(`/update-user/${userId}`, formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // No image selected, use regular JSON payload
        const payload = applyPascalNameFields({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          company_email: formData.companyEmail,
          // Support both legacy role_id and new role_ids array
          role_id: primaryRoleId != null ? primaryRoleId : undefined,
          role_ids: selectedRoleIds.length ? selectedRoleIds : undefined,
          designation_id: formData.designationId ? parseInt(formData.designationId, 10) : undefined,
          department: formData.department,
          status: formData.status || undefined,
          gender: formData.gender || undefined,
          country_code: formData.phoneNumber ? formData.countryCode : undefined,
          phone_number: formData.phoneNumber || undefined,
          alt_number: formData.altNumber || undefined,
          aadhar: formData.aadhaar || undefined,
          pan: formData.pan || undefined,
          dob: formData.dateOfBirth || undefined,
          address_line_1: formData.addressLine1 || "",
          address_line_2: formData.addressLine2 || "",
          area: formData.area || "",
          city: formData.city || "",
          state: formData.state || "",
          country: formData.country || "",
          pincode: formData.pincode || "",
          company_name: formData.company || undefined,
          company_client_id: formData.companyClientId || undefined,
          band: formData.band || undefined,
          doj: formData.dateOfJoining || undefined,
          date_of_joining: formData.dateOfJoining || undefined,
          date_of_leaving: formData.dateOfLeaving || undefined,
          company_location: formData.companyLocation || undefined
        });
        if (formData.password) {
          payload.password = formData.password;
        }
        Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
        
        response = await axiosInstance.put(`/update-user/${userId}`, payload);
      }
      
      if (response.data) {
        console.log("User updated successfully:", response.data);
        
        // Update localStorage with updated user data if this is the current user
        try {
          const currentUserStr = getAuthItem("user");
          if (currentUserStr) {
            const currentUser = JSON.parse(currentUserStr);
            // If updating the current logged-in user, update localStorage
            if (currentUser.user_id === userId) {
              // Check if response contains user data
              if (response.data.user_id) {
                // Response is the user object directly
                setAuthItem("user", JSON.stringify(response.data));
                // Dispatch custom event to notify TopHeader
                window.dispatchEvent(new Event('userDataUpdated'));
              } else if (response.data.data && response.data.data.user) {
                // Response is wrapped in data.data.user
                setAuthItem("user", JSON.stringify(response.data.data.user));
                // Dispatch custom event to notify TopHeader
                window.dispatchEvent(new Event('userDataUpdated'));
              }
            }
          }
        } catch (err) {
          console.error("Error updating localStorage:", err);
        }

        const reportingError = await reportingRef.current?.save(userId);
        alert(reportingError ? `User updated, but the reporting line was not saved: ${reportingError}` : "User updated successfully!");
        
        // Call parent callback to refresh users list
        if (onUpdate) {
          onUpdate();
        }

        // Navigate back to users page
        if (onBack) {
          onBack();
        }
      }
    } catch (error) {
      console.error("Error updating user:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          "Failed to update user. Please try again.";
      setError(errorMessage);
      alert(`Error: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="adduser-container">
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          Loading user details...
        </div>
      </div>
    );
  }

  return (
    <div className="adduser-container">
      <button className="back-link" onClick={onBack}>
        <FaArrowLeft /> Back 
      </button>
      <form className="adduser-form" onSubmit={handleSubmit}>
        {error && <div className="error-message" style={{ 
          backgroundColor: '#fee', 
          color: '#c33', 
          padding: '12px', 
          borderRadius: '4px', 
          marginBottom: '20px',
          fontSize: '14px'
        }}>{error}</div>}
        
        {/* Personal Information */}
        <div className="form-section">
          <h2 className="section-title">Personal Information</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>First Name <span className="required">*</span></label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name <span className="required">*</span></label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Gender</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
              >
                {genders.map(gender => (
                  <option key={gender} value={gender === "Select Gender" ? "" : gender}>
                    {gender}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Aadhaar Number <span className="required">*</span></label>
              <input
                type="text"
                name="aadhaar"
                value={formData.aadhaar}
                onChange={handleInputChange}
                inputMode="numeric"
                maxLength={14}
                placeholder="xxxx xxxx xxxx"
                required
              />
              {formData.aadhaar && aadhaarError(formData.aadhaar) ? (
                <span className="adduser-field-error">{aadhaarError(formData.aadhaar)}</span>
              ) : null}
            </div>
            <div className="form-group">
              <label>PAN</label>
              <input
                type="text"
                name="pan"
                value={formData.pan}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Date of Birth</label>
              <div className="date-input-wrapper">
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  ref={dateOfBirthRef}
                />
                <FaCalendarAlt 
                  className="calendar-icon" 
                  onClick={() => dateOfBirthRef.current?.showPicker()}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Personal Phone</label>
              <div className="adduser-phone-row">
                <CountryCodeSelect
                  value={formData.countryCode}
                  onChange={(code) => setFormData((prev) => ({ ...prev, countryCode: code }))}
                  ariaLabel="Personal phone country code"
                />
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  inputMode="numeric"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Alt Phone</label>
              <div className="adduser-phone-row">
                <CountryCodeSelect
                  value={formData.altCountryCode}
                  onChange={(code) => setFormData((prev) => ({ ...prev, altCountryCode: code }))}
                  ariaLabel="Alt phone country code"
                />
                <input
                  type="tel"
                  name="altNumber"
                  value={formData.altNumber}
                  onChange={handleInputChange}
                  inputMode="numeric"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Personal Email <span className="required">*</span></label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
              {personalEmailError(formData.email, formData.companyEmail) ? (
                <span className="adduser-field-error">
                  {personalEmailError(formData.email, formData.companyEmail)}
                </span>
              ) : null}
            </div>
            <GoogleAddressSearch
              value={{
                addressLine1: formData.addressLine1,
                addressLine2: formData.addressLine2,
                area: formData.area,
                city: formData.city,
                state: formData.state,
                country: formData.country,
                pincode: formData.pincode,
                formattedAddress: formData.formattedAddress,
              }}
              onChange={(next) =>
                setFormData((prev) => ({
                  ...prev,
                  addressLine1: next.addressLine1 || "",
                  addressLine2: next.addressLine2 || "",
                  area: next.area || "",
                  city: next.city || "",
                  state: next.state || "",
                  country: next.country || "",
                  pincode: next.pincode || "",
                  formattedAddress: next.formattedAddress || "",
                }))
              }
            />
          </div>
        </div>

        {/* Professional Information */}
        <div className="form-section">
          <h2 className="section-title">Professional Information</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Company</label>
              <TenantCompanySelect
                value={formData.company}
                onChange={handleInputChange}
                onTenantChange={(id) => setFormData((prev) => ({ ...prev, companyClientId: id }))}
              />
            </div>
            <div className="form-group">
              <label>Company Email <span className="required">*</span></label>
              <input
                type="email"
                name="companyEmail"
                value={formData.companyEmail}
                onChange={handleInputChange}
                required
              />
            </div>
            <OwnerPasswordField
              label="Reset Password"
              value={formData.password}
              onChange={(password) => setFormData((prev) => ({ ...prev, password }))}
              placeholder="Leave blank to keep current"
              inlineShare
              hint={PASSWORD_POLICY_HINT}
              loginEmail={String(formData.companyEmail || "").trim()}
              portalUrl={`${window.location.origin}${getStaffLoginUrl()}`}
              recipientName={String(formData.firstName || "").trim()}
              recipientEmail={String(formData.email || "").trim()}
              recipientPhone={formData.phoneNumber ? `${formData.countryCode}${formData.phoneNumber}` : ""}
              saveNote="Click Save first so this password is active."
              missingNote="Enter the Company Email and a new password to share the login."
            />
            <div className="form-group">
              <label>Employee ID</label>
              <input
                type="text"
                name="employeeId"
                value={formData.employeeId}
                placeholder="Auto-generated on save"
                readOnly
                disabled
              />
            </div>
            <div className="form-group role-multiselect-group">
              <label>Role <span className="required">*</span></label>
              <div
                className={`multi-select-trigger ${isRoleDropdownOpen ? "open" : ""}`}
                onClick={() => setIsRoleDropdownOpen((open) => !open)}
              >
                <span className={formData.role ? "" : "placeholder-text-dark"}>
                  {roleDisplayName(formData.role) || "Select Role"}
                </span>
                <FaChevronDown className="multi-select-arrow" />
              </div>
              {isRoleDropdownOpen && (
                <div className="multi-select-panel">
                  <label className="multi-select-item">
                    <input
                      type="checkbox"
                      checked={
                        Array.isArray(formData.roleIds) &&
                        formData.roleIds.length === rolesList.length &&
                        rolesList.length > 0
                      }
                      onChange={handleToggleSelectAllRoles}
                    />
                    <span>Select all</span>
                  </label>
                  {rolesList.map((r) => {
                    const idStr = String(r.role_id);
                    const checked =
                      Array.isArray(formData.roleIds) &&
                      formData.roleIds.includes(idStr);
                    return (
                      <label key={r.role_id} className="multi-select-item">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleToggleRole(idStr)}
                        />
                        <span>{roleDisplayName(r.role_name)}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Designation</label>
              <div className="domain-filter-dropdown" ref={designationDropdownRef} style={{ minWidth: "100%" }}>
                <div
                  className="search-filter-input search-filter-select domain-filter-trigger"
                  style={{
                    width: "100%",
                    height: 42,
                    borderRadius: 5,
                    border: "1px solid #D6E0EA",
                    padding: "0 12px",
                    fontSize: 14,
                    background: "#fff",
                    boxSizing: "border-box"
                  }}
                  onClick={() => setDesignationDropdownOpen((o) => !o)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setDesignationDropdownOpen((o) => !o);
                    }
                  }}
                >
                  <span className={formData.designationId ? "" : "placeholder-text-dark"}>
                    {formData.designationId
                      ? (() => {
                          const sel = designationsList.find(
                            (d) => String(d.designation_id) === String(formData.designationId)
                          );
                          return sel ? sel.designation_name : "Select Designation";
                        })()
                      : "Select Designation"}
                  </span>
                  <FaChevronDown
                    className={`domain-filter-chevron ${designationDropdownOpen ? "open" : ""}`}
                  />
                </div>
                {designationDropdownOpen && (
                  <div className="domain-filter-panel">
                    <input
                      type="text"
                      className="domain-filter-search"
                  
                      value={designationSearchQuery}
                      onChange={(e) => setDesignationSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                    <div className="domain-filter-options">
                      <div
                        className={`domain-filter-option ${!formData.designationId ? "selected" : ""}`}
                        onClick={() => {
                          setFormData((p) => ({ ...p, designationId: "" }));
                          setDesignationDropdownOpen(false);
                          setDesignationSearchQuery("");
                        }}
                      >
                        Select Designation
                      </div>
                      {designationsList
                        .filter((d) => {
                          const q = designationSearchQuery.trim().toLowerCase();
                          if (!q) return true;
                          return String(d.designation_name || "")
                            .toLowerCase()
                            .includes(q);
                        })
                        .map((d) => (
                          <div
                            key={d.designation_id}
                            className={`domain-filter-option ${
                              String(formData.designationId) === String(d.designation_id) ? "selected" : ""
                            }`}
                            onClick={() => {
                              setFormData((p) => ({
                                ...p,
                                designationId: String(d.designation_id)
                              }));
                              setDesignationDropdownOpen(false);
                              setDesignationSearchQuery("");
                            }}
                          >
                            {d.designation_name}
                          </div>
                        ))}
                      {designationsList.filter((d) => {
                        const q = designationSearchQuery.trim().toLowerCase();
                        if (!q) return true;
                        return String(d.designation_name || "")
                          .toLowerCase()
                          .includes(q);
                      }).length === 0 && (
                        <div className="domain-filter-no-results">No designations found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="emp-department">Department <span className="required">*</span></label>
              <DepartmentSelect
                id="emp-department"
                api={staffWorkApi}
                value={formData.department}
                onChange={(name) => setFormData((p) => ({ ...p, department: name }))}
                required
              />
            </div>
            <ReportingLineFields ref={reportingRef} api={staffWorkApi} userId={userId} />
            <div className="form-group">
              <label>Band</label>
              <select
                name="band"
                value={formData.band}
                onChange={handleInputChange}
              >
                {bands.map(band => (
                  <option key={band} value={band === "Select Band" ? "" : band}>
                    {band}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Company Location</label>
              <input
                type="text"
                name="companyLocation"
                value={formData.companyLocation}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Date of Joining</label>
              <div className="date-input-wrapper">
                <input
                  type="date"
                  name="dateOfJoining"
                  value={formData.dateOfJoining}
                  onChange={handleInputChange}
                  ref={dateOfJoiningRef}
                />
                <FaCalendarAlt 
                  className="calendar-icon" 
                  onClick={() => dateOfJoiningRef.current?.showPicker()}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                {statuses.map(status => (
                  <option key={status} value={status === "Select Status" ? "" : status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                Date of Leaving
                {LEAVING_REQUIRED_STATUSES.includes(formData.status) ? (
                  <span className="required"> *</span>
                ) : null}
              </label>
              <div className="date-input-wrapper">
                <input
                  type="date"
                  name="dateOfLeaving"
                  value={formData.dateOfLeaving}
                  onChange={handleInputChange}
                  ref={dateOfLeavingRef}
                  required={LEAVING_REQUIRED_STATUSES.includes(formData.status)}
                />
                <FaCalendarAlt 
                  className="calendar-icon" 
                  onClick={() => dateOfLeavingRef.current?.showPicker()}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Profile Image</label>
              <div className="file-input-wrapper">
                <input
                  type="file"
                  name="profileImage"
                  id="profileImage"
                  accept="image/*"
                  onChange={handleInputChange}
                  style={{ display: "none" }}
                />
                <label htmlFor="profileImage" className="file-input-label">
                  {formData.profileImage || formData.currentImageUrl ? "Change File" : "Choose File"}
                </label>
                <span className="file-name">
                  {formData.profileImage 
                    ? formData.profileImage.name 
                    : formData.currentImageUrl 
                      ? formData.currentImageUrl.split('/').pop() || formData.currentImageUrl
                      : "No file chosen"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="adduser-form-actions">
          <button type="submit" className="btn-register" disabled={submitting}>
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditUser;
